import React from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import BugMark from '../bz/BugMark';
import ComingSoon from '../bz/ComingSoon';
import {
  IcoArrowR,
  IcoBell,
  IcoBuilding,
  IcoCheck,
  IcoFile,
  IcoFilter,
  IcoPlug,
  IcoSearch,
  IcoShield,
  IcoSlash,
  IcoSpark,
  IcoUsers,
  IcoX
} from '../bz/icons';

// ----------------------------------------------------------------------
// Small primitives used across sections
// ----------------------------------------------------------------------

const SettingRow = ({ label, hint, children }) => (
  <div
    className="bz-row"
    style={{
      padding: '14px 0',
      borderBottom: '1px solid var(--line-soft)',
      gap: 24,
      alignItems: 'flex-start'
    }}
  >
    <div style={{ flex: '0 0 240px' }}>
      <div style={{ fontSize: 'var(--fs-md)', fontWeight: 500 }}>{label}</div>
      {hint && (
        <div className="bz-muted" style={{ fontSize: 'var(--fs-sm)', marginTop: 4 }}>
          {hint}
        </div>
      )}
    </div>
    <div className="bz-grow">{children}</div>
  </div>
);
SettingRow.propTypes = { label: PropTypes.string, hint: PropTypes.string, children: PropTypes.node };

const Toggle = ({ on, label }) => (
  <div className="bz-row" style={{ gap: 10 }}>
    <span
      style={{
        width: 32,
        height: 18,
        borderRadius: 999,
        background: on ? 'var(--bz-orange-1)' : 'var(--line-strong)',
        position: 'relative',
        flexShrink: 0,
        transition: 'background .12s'
      }}
    >
      <span
        style={{
          position: 'absolute',
          top: 2,
          left: on ? 16 : 2,
          width: 14,
          height: 14,
          borderRadius: 999,
          background: 'white',
          boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
          transition: 'left .12s'
        }}
      />
    </span>
    {label && <span style={{ fontSize: 'var(--fs-md)' }}>{label}</span>}
  </div>
);
Toggle.propTypes = { on: PropTypes.bool, label: PropTypes.string };

// ----------------------------------------------------------------------
// Section bodies
// ----------------------------------------------------------------------

const SettingsOrganization = () => (
  <div style={{ display: 'flex', flexDirection: 'column' }}>
    <SettingRow label="Display name" hint="Shown in the header, emails and reports.">
      <input className="bz-input" defaultValue="findbugzero" style={{ width: 320, padding: '7px 10px' }} />
    </SettingRow>
    <SettingRow label="Tenant slug" hint="Used in URLs and API tokens. Lowercase letters, numbers and hyphens only.">
      <div className="bz-row" style={{ gap: 6 }}>
        <span className="bz-muted bz-mono" style={{ fontSize: 'var(--fs-sm)' }}>
          portal.bugzero.io/
        </span>
        <input className="bz-input" defaultValue="findbugzero" style={{ width: 220, padding: '7px 10px' }} />
      </div>
    </SettingRow>
    <SettingRow
      label="Environment"
      hint="Used as a visible chip in the header so prod and dev tenants are unmistakable."
    >
      <div className="bz-row" style={{ gap: 6 }}>
        {['Production', 'Staging', 'Development'].map((e, i) => (
          <span
            key={e}
            className={`bz-pill ${i === 0 ? 'bz-pill--ok' : ''}`}
            style={{
              cursor: 'pointer',
              padding: '4px 10px',
              fontSize: 'var(--fs-sm)',
              border: i === 0 ? '1px solid var(--ok)' : '1px solid var(--line)',
              background: i === 0 ? 'var(--ok-soft)' : 'transparent'
            }}
          >
            {e}
          </span>
        ))}
      </div>
    </SettingRow>
    <SettingRow label="Logo" hint="PNG or SVG. Used in the sidebar, exports and emails.">
      <div className="bz-row" style={{ gap: 12 }}>
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: 8,
            background: 'var(--bg-tan)',
            border: '1px solid var(--line)',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <BugMark size={32} />
        </div>
        <button type="button" className="bz-btn bz-btn--sm">
          Upload custom logo
        </button>
        <button type="button" className="bz-btn bz-btn--sm bz-btn--ghost">
          Reset to BugZero
        </button>
      </div>
    </SettingRow>
    <SettingRow label="Time zone" hint="Used for timestamps in tables, reports and notifications.">
      <select className="bz-input" defaultValue="America/Denver" style={{ width: 320, padding: '7px 10px' }}>
        <option>America/Denver — MDT (UTC−06:00)</option>
        <option>America/Los_Angeles — PDT (UTC−07:00)</option>
        <option>America/New_York — EDT (UTC−04:00)</option>
        <option>UTC</option>
      </select>
    </SettingRow>
    <div className="bz-row" style={{ paddingTop: 20, justifyContent: 'flex-end', gap: 8 }}>
      <button type="button" className="bz-btn">
        Cancel
      </button>
      <button type="button" className="bz-btn bz-btn--accent">
        Save changes
      </button>
    </div>
  </div>
);

const SettingsMembers = () => {
  const members = [
    ['CK', 'Chase Kanstrup', 'chase@findbugzero.com', 'Owner', 'Active', '12m ago'],
    ['KT', 'Kira Tan', 'k.tan@findbugzero.com', 'Admin', 'Active', '2h ago'],
    ['MD', 'Marcus Diaz', 'marcus@findbugzero.com', 'Engineer', 'Active', '1d ago'],
    ['SA', 'Samira Ahmed', 's.ahmed@findbugzero.com', 'Engineer', 'Active', '1d ago'],
    ['JR', 'James Rourke', 'james@findbugzero.com', 'Auditor', 'Active', '4d ago'],
    ['NN', 'Nia Njoroge', 'nia@findbugzero.com', 'Engineer', 'Pending', '—']
  ];
  const colors = [
    'var(--bz-blue-1)',
    'var(--bz-orange-1)',
    'var(--bz-blue-2)',
    'var(--bz-orange-2)',
    'var(--predict)',
    'var(--bz-blue-3)'
  ];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div className="bz-row" style={{ gap: 8, justifyContent: 'space-between' }}>
        <div className="bz-row" style={{ gap: 8 }}>
          <span className="bz-input" style={{ width: 280, padding: '6px 10px' }}>
            <IcoSearch size={13} style={{ color: 'var(--ink-muted)' }} />
            <input placeholder="Search members…" />
          </span>
          <button type="button" className="bz-btn bz-btn--sm bz-btn--ghost">
            <IcoFilter size={13} /> Filter
          </button>
        </div>
        <div className="bz-row" style={{ gap: 8 }}>
          <button type="button" className="bz-btn bz-btn--sm">
            Manage groups
          </button>
          <button type="button" className="bz-btn bz-btn--sm bz-btn--accent">
            Invite member
          </button>
        </div>
      </div>
      <div className="bz-card">
        <table className="bz-table">
          <thead>
            <tr>
              <th>Member</th>
              <th>Role</th>
              <th>Groups</th>
              <th>Status</th>
              <th>Last active</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {members.map(([init, name, email, role, status, when], i) => (
              <tr key={email}>
                <td>
                  <div className="bz-row" style={{ gap: 10 }}>
                    <div
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: 999,
                        background: colors[i % colors.length],
                        color: 'white',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 11,
                        fontWeight: 600
                      }}
                    >
                      {init}
                    </div>
                    <div>
                      <div style={{ fontWeight: 500 }}>{name}</div>
                      <div className="bz-muted" style={{ fontSize: 'var(--fs-xs)' }}>
                        {email}
                      </div>
                    </div>
                  </div>
                </td>
                <td>
                  <span
                    className={`bz-pill ${
                      role === 'Owner' ? 'bz-pill--prevent' : role === 'Admin' ? 'bz-pill--info' : ''
                    }`}
                  >
                    {role}
                  </span>
                </td>
                <td className="bz-muted bz-truncate" style={{ maxWidth: 200 }}>
                  {role === 'Owner' ? 'All' : role === 'Auditor' ? 'Auditors' : 'Engineers · Plan reviewers'}
                </td>
                <td>
                  <span className={`bz-pill ${status === 'Active' ? 'bz-pill--ok' : 'bz-pill--warn'}`}>
                    <span className="bz-dot" />
                    {status}
                  </span>
                </td>
                <td className="bz-muted">{when}</td>
                <td style={{ textAlign: 'right' }}>
                  <a style={{ fontSize: 'var(--fs-sm)' }}>Manage</a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const SettingsSSO = () => (
  <div style={{ display: 'flex', flexDirection: 'column' }}>
    <SettingRow
      label="Identity provider"
      hint="Production tenants must use SSO. Local password sign-in is disabled when SSO is enforced."
    >
      <div className="bz-row" style={{ gap: 12, alignItems: 'center' }}>
        <div
          style={{
            padding: '10px 14px',
            borderRadius: 8,
            border: '1px solid var(--line)',
            display: 'flex',
            alignItems: 'center',
            gap: 10
          }}
        >
          <div
            style={{
              width: 28,
              height: 28,
              background: '#007DC1',
              borderRadius: 6,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontWeight: 700,
              fontSize: 12
            }}
          >
            O
          </div>
          <div>
            <div style={{ fontWeight: 500 }}>Okta</div>
            <div className="bz-muted bz-mono" style={{ fontSize: 'var(--fs-xs)' }}>
              findbugzero.okta.com
            </div>
          </div>
          <span className="bz-pill bz-pill--ok" style={{ marginLeft: 8 }}>
            <span className="bz-dot" />
            Connected
          </span>
        </div>
        <button type="button" className="bz-btn bz-btn--sm">
          Change provider
        </button>
      </div>
    </SettingRow>
    <SettingRow label="Enforce SSO" hint="Require all members to sign in via the identity provider above.">
      <Toggle on label="Enforced for all members" />
    </SettingRow>
    <SettingRow
      label="SCIM provisioning"
      hint="Auto-provision and de-provision members based on directory group membership."
    >
      <Toggle on label="Active — last sync 4 min ago" />
    </SettingRow>
    <SettingRow
      label="Just-in-time creation"
      hint="Create an account on first successful SSO sign-in if it does not already exist."
    >
      <Toggle on={false} label="Off" />
    </SettingRow>
    <SettingRow label="Group mapping" hint="Map identity-provider groups to BugZero roles. Applied at next sign-in.">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {[
          ['bugzero-admins', 'Admin'],
          ['bugzero-engineers', 'Engineer'],
          ['security-readonly', 'Auditor']
        ].map(([dir, role]) => (
          <div key={dir} className="bz-row" style={{ gap: 10, fontSize: 'var(--fs-sm)' }}>
            <span className="bz-mono" style={{ background: 'var(--bg-tan)', padding: '2px 8px', borderRadius: 4 }}>
              {dir}
            </span>
            <IcoArrowR size={12} style={{ color: 'var(--ink-muted)' }} />
            <span style={{ fontWeight: 500 }}>{role}</span>
          </div>
        ))}
        <a
          style={{
            fontSize: 'var(--fs-sm)',
            color: 'var(--info)',
            marginTop: 4,
            cursor: 'pointer'
          }}
        >
          + Add mapping
        </a>
      </div>
    </SettingRow>
  </div>
);

const SettingsIntegrations = () => {
  const list = [
    {
      name: 'ServiceNow',
      desc: 'Route findings and Plan reviews to ServiceNow incidents.',
      status: 'Connected',
      meta: 'instance: findbugzero.service-now.com'
    },
    {
      name: 'Splunk',
      desc: 'Stream audit and findings events to your Splunk index.',
      status: 'Connected',
      meta: 'index: bugzero_prod · last event 12s ago'
    },
    {
      name: 'BugZero API',
      desc: 'Programmatic access to all platform resources.',
      status: 'Connected',
      meta: '2 active tokens · 0 expiring in 30d'
    },
    {
      name: 'Webhooks',
      desc: 'Outbound HTTP callbacks for events of your choosing.',
      status: 'Off',
      meta: 'No endpoints configured'
    },
    {
      name: 'PagerDuty',
      desc: 'Page on-call engineers for severe findings.',
      status: 'Available',
      meta: 'Available on Enterprise plan'
    },
    {
      name: 'Slack',
      desc: 'Post platform and product events to Slack channels.',
      status: 'Available',
      meta: ''
    }
  ];
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
      {list.map(i => {
        const tone = i.status === 'Connected' ? 'bz-pill--ok' : i.status === 'Off' ? 'bz-pill--warn' : '';
        return (
          <div key={i.name} className="bz-card" style={{ padding: 18 }}>
            <div
              className="bz-row"
              style={{ justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 6 }}
            >
              <div className="bz-row" style={{ gap: 10 }}>
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 8,
                    background: 'var(--bg-tan)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--bz-blue-2)'
                  }}
                >
                  <IcoPlug size={18} />
                </div>
                <div>
                  <div className="bz-h3">{i.name}</div>
                  <div className="bz-muted" style={{ fontSize: 'var(--fs-xs)', marginTop: 2 }}>
                    {i.meta}
                  </div>
                </div>
              </div>
              <span className={`bz-pill ${tone}`}>
                {i.status === 'Connected' && <span className="bz-dot" />}
                {i.status}
              </span>
            </div>
            <p style={{ margin: '10px 0 14px', color: 'var(--ink-soft)', fontSize: 'var(--fs-sm)', lineHeight: 1.5 }}>
              {i.desc}
            </p>
            <div className="bz-row" style={{ gap: 8 }}>
              {i.status === 'Connected' ? (
                <>
                  <button type="button" className="bz-btn bz-btn--sm">
                    Configure
                  </button>
                  <button type="button" className="bz-btn bz-btn--sm bz-btn--ghost">
                    Disconnect
                  </button>
                </>
              ) : i.status === 'Available' ? (
                <button type="button" className="bz-btn bz-btn--sm bz-btn--primary">
                  Connect
                </button>
              ) : (
                <button type="button" className="bz-btn bz-btn--sm">
                  Set up
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const SettingsNotifications = () => (
  <div style={{ display: 'flex', flexDirection: 'column' }}>
    <SettingRow
      label="Default email recipients"
      hint="Used when a product-level notification rule does not specify its own recipients."
    >
      <div className="bz-row" style={{ gap: 6, flexWrap: 'wrap' }}>
        {['ops@findbugzero.com', 'security-leads@findbugzero.com'].map(e => (
          <span key={e} className="bz-pill" style={{ background: 'var(--bg-tan)', color: 'var(--ink)' }}>
            {e} <IcoX size={10} />
          </span>
        ))}
        <input
          className="bz-input"
          placeholder="+ add address"
          style={{ width: 200, padding: '4px 8px', minHeight: 28 }}
        />
      </div>
    </SettingRow>
    <SettingRow
      label="Severity threshold"
      hint="The minimum severity that produces a notification. Each product can override this."
    >
      <select className="bz-input" defaultValue="High" style={{ width: 220, padding: '7px 10px' }}>
        <option>Severe and above</option>
        <option>High and above</option>
        <option>Moderate and above</option>
        <option>Everything</option>
      </select>
    </SettingRow>
    <SettingRow
      label="Channels"
      hint="Where notifications are delivered. Per-channel routing rules live in Integrations."
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <Toggle on label="Email" />
        <Toggle on label="ServiceNow incidents" />
        <Toggle on={false} label="Splunk audit stream" />
        <Toggle on={false} label="Webhook · #ops-alerts" />
      </div>
    </SettingRow>
    <SettingRow
      label="Quiet hours"
      hint="Suppress non-severe notifications outside business hours. Severe findings always notify."
    >
      <div className="bz-row" style={{ gap: 10 }}>
        <Toggle on />
        <span className="bz-muted" style={{ fontSize: 'var(--fs-sm)' }}>
          22:00 – 06:00 · weekdays
        </span>
      </div>
    </SettingRow>
    <div className="bz-row" style={{ paddingTop: 20, justifyContent: 'flex-end', gap: 8 }}>
      <button type="button" className="bz-btn">
        Cancel
      </button>
      <button type="button" className="bz-btn bz-btn--accent">
        Save changes
      </button>
    </div>
  </div>
);

const SettingsAudit = () => {
  const rows = [
    ['12m ago', 'Chase Kanstrup', 'plan.project.accepted', 'project=cisco-1100-isr-17.15.4 → 17.15.5'],
    ['1h ago', 'Kira Tan', 'platform.member.invited', 'email=k.tan@findbugzero.com · group=Engineers'],
    ['2h ago', 'system', 'prevent.finding.created', 'id=CSCwt73746 · severity=Severe'],
    ['5h ago', 'Chase Kanstrup', 'platform.integration.config', 'servicenow.route.update · sev>=High'],
    ['1d ago', 'Marcus Diaz', 'platform.role.update', 'role=Engineer · grant=plan.review:write'],
    ['1d ago', 'system', 'platform.sso.sync', 'okta · users=24 added=0 removed=0'],
    ['3d ago', 'Chase Kanstrup', 'predict.tenant.optin', 'tenant=findbugzero · early-access']
  ];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div className="bz-row" style={{ gap: 8, justifyContent: 'space-between' }}>
        <div className="bz-row" style={{ gap: 6 }}>
          <span className="bz-input" style={{ width: 260, padding: '6px 10px' }}>
            <IcoSearch size={13} style={{ color: 'var(--ink-muted)' }} />
            <input placeholder="Filter by actor, event or detail…" />
          </span>
          <button type="button" className="bz-btn bz-btn--sm bz-btn--ghost">
            <IcoFilter size={13} /> Last 24 hours
          </button>
          <button type="button" className="bz-btn bz-btn--sm bz-btn--ghost">
            All products
          </button>
        </div>
        <div className="bz-row" style={{ gap: 8 }}>
          <span className="bz-muted" style={{ fontSize: 'var(--fs-sm)' }}>
            Retention: 365 days
          </span>
          <button type="button" className="bz-btn bz-btn--sm">
            Export CSV
          </button>
        </div>
      </div>
      <div className="bz-card">
        <table className="bz-table">
          <thead>
            <tr>
              <th>When</th>
              <th>Actor</th>
              <th>Event</th>
              <th>Detail</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                <td className="bz-muted bz-mono" style={{ fontSize: 'var(--fs-sm)' }}>
                  {r[0]}
                </td>
                <td style={{ fontWeight: 500 }}>{r[1]}</td>
                <td className="bz-mono" style={{ fontSize: 'var(--fs-sm)', color: 'var(--bz-blue-2)' }}>
                  {r[2]}
                </td>
                <td className="bz-muted bz-mono" style={{ fontSize: 'var(--fs-sm)' }}>
                  {r[3]}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const SettingsFirewall = () => (
  <div style={{ display: 'flex', flexDirection: 'column' }}>
    <SettingRow label="Allow list" hint="IPs or CIDRs allowed to access the portal. Empty = open to anyone with SSO.">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxWidth: 420 }}>
        {['72.18.118.0/24 — Denver HQ', '34.220.18.4 — VPN gateway', '2603:1030::/32 — Azure egress'].map(r => (
          <div
            key={r}
            className="bz-row"
            style={{
              padding: '6px 10px',
              border: '1px solid var(--line)',
              borderRadius: 6,
              fontSize: 'var(--fs-sm)',
              justifyContent: 'space-between'
            }}
          >
            <span className="bz-mono">{r}</span>
            <IcoX size={12} style={{ color: 'var(--ink-muted)', cursor: 'pointer' }} />
          </div>
        ))}
        <button type="button" className="bz-btn bz-btn--sm" style={{ alignSelf: 'flex-start', marginTop: 4 }}>
          + Add range
        </button>
      </div>
    </SettingRow>
    <SettingRow label="API access" hint="Whether the BugZero API enforces the same allow list.">
      <Toggle on label="Apply to API tokens" />
    </SettingRow>
    <SettingRow label="Block unknown countries" hint="Reject sign-ins originating from countries not on this list.">
      <div className="bz-row" style={{ gap: 10 }}>
        <Toggle on={false} />
        <span className="bz-muted" style={{ fontSize: 'var(--fs-sm)' }}>
          Currently off — sign-ins allowed globally.
        </span>
      </div>
    </SettingRow>
  </div>
);

const SettingsGlobalAI = () => (
  <div style={{ display: 'flex', flexDirection: 'column' }}>
    <SettingRow label="AI features" hint="Master switch for AI-powered features across Prevent, Plan and Predict.">
      <Toggle on label="AI features enabled across the platform" />
    </SettingRow>
    <SettingRow
      label="Model provider"
      hint="Backing inference provider for summarization, risk explanations and Predict modeling."
    >
      <select className="bz-input" defaultValue="bz" style={{ width: 320, padding: '7px 10px' }}>
        <option value="bz">BugZero-hosted (default · SOC 2 Type II)</option>
        <option value="aws-bedrock">Bring-your-own · AWS Bedrock</option>
        <option value="azure-oai">Bring-your-own · Azure OpenAI</option>
      </select>
    </SettingRow>
    <SettingRow label="Data residency" hint="Where prompts and AI outputs are processed and stored.">
      <select className="bz-input" defaultValue="us-west-2" style={{ width: 220, padding: '7px 10px' }}>
        <option value="us-west-2">United States · us-west-2</option>
        <option value="eu-central-1">European Union · eu-central-1</option>
      </select>
    </SettingRow>
    <SettingRow
      label="Training opt-out"
      hint="Exclude this tenant's data from any future model training, evaluation or fine-tuning."
    >
      <Toggle on label="Opted out — never used for training" />
    </SettingRow>
    <SettingRow
      label="Per-product overrides"
      hint="Each product may have stricter AI settings than the platform default."
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 'var(--fs-sm)' }}>
        <div className="bz-row" style={{ gap: 8 }}>
          <span className="bz-pill bz-pill--prevent">Prevent</span>
          <span className="bz-muted">inherits platform default</span>
        </div>
        <div className="bz-row" style={{ gap: 8 }}>
          <span className="bz-pill bz-pill--plan">Plan</span>
          <span className="bz-muted">inherits platform default</span>
        </div>
        <div className="bz-row" style={{ gap: 8 }}>
          <span className="bz-pill bz-pill--predict">Predict</span>
          <span className="bz-muted">inherits platform default · early access</span>
        </div>
      </div>
    </SettingRow>
  </div>
);

const SettingsBilling = () => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
    <div className="bz-card" style={{ padding: 20 }}>
      <div className="bz-row" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div className="bz-kicker">Current plan</div>
          <div className="bz-h2" style={{ marginTop: 4 }}>
            BugZero Enterprise
          </div>
          <div className="bz-muted" style={{ fontSize: 'var(--fs-sm)', marginTop: 6 }}>
            Annual contract · renews March 31, 2027 · Net-30 invoicing
          </div>
        </div>
        <button type="button" className="bz-btn">
          Manage plan
        </button>
      </div>
    </div>
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
      {[
        {
          kicker: 'Products',
          value: '3 of 3',
          sub: 'Prevent · Plan · Predict (preview)',
          accent: 'var(--bz-orange-1)'
        },
        { kicker: 'Members', value: '24 / 50', sub: '26 seats remaining', accent: 'var(--bz-blue-2)' },
        {
          kicker: 'Vendors',
          value: '15 / unlimited',
          sub: 'all upstream vendor feeds included',
          accent: 'var(--predict)'
        }
      ].map(s => (
        <div key={s.kicker} className="bz-stat" style={{ borderTop: `3px solid ${s.accent}` }}>
          <div className="bz-kicker" style={{ color: s.accent }}>
            {s.kicker}
          </div>
          <div className="bz-stat__value">{s.value}</div>
          <div className="bz-stat__meta">{s.sub}</div>
        </div>
      ))}
    </div>
    <div className="bz-card">
      <div className="bz-row" style={{ padding: '12px 16px', borderBottom: '1px solid var(--line-soft)' }}>
        <h3 className="bz-h3">Recent invoices</h3>
      </div>
      <table className="bz-table">
        <thead>
          <tr>
            <th>Invoice</th>
            <th>Period</th>
            <th>Amount</th>
            <th>Status</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {[
            ['INV-2026-005', 'May 2026', '$8,750.00', 'Paid'],
            ['INV-2026-004', 'Apr 2026', '$8,750.00', 'Paid'],
            ['INV-2026-003', 'Mar 2026 · annual', '$105,000.00', 'Paid'],
            ['INV-2025-014', 'Dec 2025', '$8,200.00', 'Paid']
          ].map(r => (
            <tr key={r[0]}>
              <td>
                <a className="bz-mono">{r[0]}</a>
              </td>
              <td className="bz-muted">{r[1]}</td>
              <td className="bz-mono">{r[2]}</td>
              <td>
                <span className="bz-pill bz-pill--ok">
                  <span className="bz-dot" />
                  {r[3]}
                </span>
              </td>
              <td style={{ textAlign: 'right' }}>
                <a style={{ fontSize: 'var(--fs-sm)' }}>Download PDF</a>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

// ----------------------------------------------------------------------
// Section index + shell
// ----------------------------------------------------------------------

// Each Platform Settings section. The Claude-design mocks (SettingsOrganization,
// SettingsMembers, …) are all static placeholders — none are wired to a real
// save endpoint yet — so every section currently ships as `status: 'coming-soon'`
// and renders the <ComingSoon> placeholder instead of the mock body.
//
// The mock `render` thunks are intentionally kept: they're the visual scaffold
// for when the feature is built. To enable a section:
//   1. Wire its Settings* component to the real API.
//   2. Flip `status` to 'available'.
//   3. Move its row in COMING_SOON.md to "Shipped".
//
// `legacyPath` points at the existing (real) page that already does this job,
// so users can reach working functionality in the meantime.
const SECTIONS = [
  {
    id: 'organization',
    label: 'Organization',
    Icon: IcoBuilding,
    status: 'coming-soon',
    summary: 'Tenant identity — display name, slug, environment, logo and time zone.',
    planned: ['Editable org profile', 'Custom logo upload', 'Per-tenant environment badge'],
    render: () => <SettingsOrganization />
  },
  {
    id: 'members',
    label: 'Members & Access',
    Icon: IcoUsers,
    status: 'coming-soon',
    summary: 'A unified members, groups and roles experience for the platform.',
    legacyPath: '/users/view-users',
    legacyLabel: 'Manage users now',
    render: () => <SettingsMembers />
  },
  {
    id: 'sso',
    label: 'SSO & Authentication',
    Icon: IcoShield,
    status: 'coming-soon',
    summary: 'Identity provider connection, SSO enforcement, SCIM and group→role mapping.',
    legacyPath: '/integrations/sso',
    legacyLabel: 'Configure SSO now',
    render: () => <SettingsSSO />
  },
  {
    id: 'integrations',
    label: 'Integrations',
    Icon: IcoPlug,
    status: 'coming-soon',
    summary: 'Cross-product integrations — ServiceNow, Splunk, BugZero API and webhooks.',
    legacyPath: '/integrations/servicenow',
    legacyLabel: 'Open integrations now',
    render: () => <SettingsIntegrations />
  },
  {
    id: 'notifications',
    label: 'Notifications',
    Icon: IcoBell,
    status: 'coming-soon',
    summary: 'Platform-wide notification defaults that each product can inherit or override.',
    planned: ['Default recipients', 'Severity thresholds', 'Channel routing', 'Quiet hours'],
    render: () => <SettingsNotifications />
  },
  {
    id: 'audit',
    label: 'Audit log',
    Icon: IcoFile,
    status: 'coming-soon',
    summary: 'A cross-product event stream with search and CSV export.',
    planned: ['Requires a backend audit/events API'],
    render: () => <SettingsAudit />
  },
  {
    id: 'firewall',
    label: 'Firewall',
    Icon: IcoSlash,
    status: 'coming-soon',
    summary: 'IP / CIDR allow-list controlling access to the portal and API.',
    legacyPath: '/admin/firewall',
    legacyLabel: 'Open firewall now',
    render: () => <SettingsFirewall />
  },
  {
    id: 'globalai',
    label: 'Global AI',
    Icon: IcoSpark,
    status: 'coming-soon',
    summary: 'Platform-wide AI provider, data residency and training opt-out, with per-product overrides.',
    legacyPath: '/admin/global-ai',
    legacyLabel: 'Open Global AI now',
    render: () => <SettingsGlobalAI />
  },
  {
    id: 'billing',
    label: 'Billing & Plan',
    Icon: IcoFile,
    status: 'coming-soon',
    summary: 'Plan summary, seat usage and invoices.',
    planned: ['Requires billing/subscription API'],
    render: () => <SettingsBilling />
  }
];

const PlatformSettings = () => {
  const { section: routeSection } = useParams();
  const active = SECTIONS.find(s => s.id === routeSection) || SECTIONS[0];

  // Section navigation lives in the shell sidebar (Platform group) — this page
  // renders only the active section's header + body. No inner rail, so the
  // settings area shows a single side nav, not two nested ones.
  return (
    <div style={{ padding: '28px 36px' }}>
      <div style={{ maxWidth: 880, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 20 }}>
        <div className="bz-row" style={{ justifyContent: 'space-between', alignItems: 'flex-end' }}>
          <div>
            <div className="bz-kicker">BugZero Platform</div>
            <h1 className="bz-h1" style={{ marginTop: 4 }}>
              {active.label}
            </h1>
            <div className="bz-muted" style={{ fontSize: 'var(--fs-md)', marginTop: 4 }}>
              Applies to all products in this tenant — Prevent, Plan and Predict.
            </div>
          </div>
          <span className="bz-pill bz-pill--info">
            <IcoCheck size={11} />
            Platform-wide
          </span>
        </div>
        {active.status === 'available' ? (
          active.render()
        ) : (
          <ComingSoon
            title={active.label}
            summary={active.summary}
            planned={active.planned}
            legacyPath={active.legacyPath}
            legacyLabel={active.legacyLabel}
          />
        )}
      </div>
    </div>
  );
};

export default PlatformSettings;
